//---------------------------------------------------
//  Nom du fichier : Map.js 
//
//  Description: Component Map qui contient la map de leaflet
//               Contient la logique de cluster de marqueur
//               Contient la logique de localiser l'utilisateur
//               Contient la logique de routing machine (Destination - itinéraire)
//               Contient la logique de choisir un emplacement pour la publication
//               Contient la page d'information du site
//
//  Auteur: Alek Del Balso et Vega Laliberté
//---------------------------------------------------

import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import { useState, useEffect } from "react";
import './Map.css';
import 'leaflet/dist/leaflet.css';
import RoutingMachine from "./RoutingMachine";
import "./RoutingMachine.css";
import 'leaflet-providers';
import {createColorIcon, createClusterCustomIcon} from './Icon'
import MarkerClusterGroup from 'react-leaflet-markercluster';
import { useTranslation } from "react-i18next";
import Button from '@mui/material/Button';


function HoverMarker({ position }) {
    return position ? (
        <Marker position={position} icon={createColorIcon(require('./Images/highlight_icon.png'))} />
    ) : null;
}

function SousInfo({ subject, imgName, reverse=false}){
    const style = {
        display: 'flex',
        flexDirection: reverse ? 'row-reverse' : 'row'
    }

    return(
        <div className='sousSectionGroup' style={style}>
            <h2 className='subjectInformationSite'>{subject}</h2>
            <span id='imageSousSection' className="material-symbols-outlined">{imgName}</span>
        </div>
    );
}


function Legend({amGrade, euGrade, color}){
    return(
        <div className='LegendSection'>
            <div className='textLegend'>{amGrade}</div>
            <div className='colorCircleLegend' style={{backgroundColor:color}}></div>
            <div className='textLegend'>{euGrade}</div>
        </div>
    )
}

function Info({ setInfo }){
    const { t } = useTranslation();
    return (
        <div className='informationSite'>
            <div className='headerInformationSite'>
                <h1 className='h1Rocks'>Rocks To Climb</h1>
                <div className="imageRocks"></div>
            </div>
            <SousInfo subject={t("infoInformation")}      imgName={"info"}/>
            <SousInfo subject={t("languageInformation")}  imgName={"language"}  reverse={true}/>
            <SousInfo subject={t("locationInformation")}  imgName={"location_on"}/>
            <SousInfo subject={t("chatInformation")}      imgName={"chat"}      reverse={true}/>
            <SousInfo subject={t("searchInformation")}    imgName={"search"}/>
            <SousInfo subject={t("handshakeInformation")} imgName={"handshake"} reverse={true}/>
            <SousInfo subject={t("pubInformation")}       imgName={"menu_book"}/>
            <SousInfo subject={t("favoriteInformation")}  imgName={"favorite"}  reverse={true}/>
            <SousInfo subject={t("registerInformation")}  imgName={"bookmarks"}/>
            <SousInfo subject={t("doneInformation")}      imgName={"verified"}  reverse={true}/>
            <SousInfo subject={t("signalInformation")}    imgName={"flag"}/>
            <SousInfo subject={t("settingInformation")}   imgName={"settings"}  reverse={true}/>
            <div className='GradeLegendsSection'>Grade Legend</div>
            <Legend amGrade={"VB V0 V1"} euGrade={"4- 4 4+"} color={"#4caf50"}></Legend>
            <Legend amGrade={"V2 V3"} euGrade={"5+ 6A 6A+"} color={"#007bff"}></Legend>
            <Legend amGrade={"V4 V5"} euGrade={"6B 6B+ 6C 6C+"} color={"#b44fc1"}></Legend>
            <Legend amGrade={"V6 V7"} euGrade={"7A 7A+"} color={"#f4e04d"}></Legend>
            <Legend amGrade={"V8 V9"} euGrade={"7B 7C"} color={"#ff8c00"}></Legend>
            <Legend amGrade={"V10 V11"} euGrade={"7C+ 8A"} color={"#ff4f58"}></Legend>
            <Legend amGrade={"V12 V13"} euGrade={"8A+ 8B"} color={"#ff69b4"}></Legend>
            <Legend amGrade={"V14 V15"} euGrade={"8B+ 8C"} color={"#50c8cc"}></Legend>
            <Legend amGrade={"V16 V17"} euGrade={"8C+ 9A"} color={"#d5b263"}></Legend>
            <span id="quit" className="material-symbols-outlined" style={{position:'fixed'}} onClick={() => setInfo(false)}>close</span>
        </div>
    );
}



//SOURCE : https://leafletjs.com/reference.html

function Map({ openPublication, isLoggedIn, set_create_publication, setCreatingMode, 
                creatingMode, creatingPublication, destination, setDestination, 
                markers, publication, mapInstance, setMapInstance,post_id, position, setPosition}) {

    const [directions, setDirections] = useState([]);
    const [hoverPosition, setHoverPosition] = useState(null);
    const [mouseHoverPosition, setMouseHoverPosition] = useState(null);

    const [showRoute, setShowRoute] = useState(true);
    const { t, i18n } = useTranslation();
    const [dayMode, setDayMode] = useState( (localStorage.getItem("daymode") == undefined ? true: (localStorage.getItem("daymode"))))
    const [info, setInfo] = useState(false)
    
    //CHECK IF CLICK
    useEffect(() => {
        if (!mapInstance) return;

        const handleClick = (e) => {
            if(creatingMode){
                setCreatingMode(false)
                set_create_publication({"pos": e.latlng})
            }
        };

        mapInstance.on('click', handleClick);

        return () => { mapInstance.off('click', handleClick); };
    }, [mapInstance, creatingMode]);

    
    useEffect(() => {
        if (!mapInstance) return;
        mapInstance.locate();
        if(publication)return
        const onLocationFound = (e) => {
            setPosition(e.latlng);
            if(post_id)return
            mapInstance.flyTo(e.latlng, mapInstance.getZoom());
        };

        mapInstance.on('locationfound', onLocationFound);

        const updateHoverPosition = (e) => {
            const latlng = mapInstance.containerPointToLatLng([e.clientX, e.clientY]);
            setMouseHoverPosition(latlng);
        };
        mapInstance.getContainer().addEventListener('mousemove', updateHoverPosition);
  
        return () => {
            mapInstance.off('locationfound', onLocationFound);
            mapInstance.getContainer().removeEventListener('mousemove', updateHoverPosition);
        };
    }, [mapInstance]);

    useEffect(() => {
        if(destination == null)
            setDirections([])
    },[destination])

    useEffect(() => {
        localStorage.setItem("daymode",dayMode);
        if (dayMode) {
            document.documentElement.classList.add('day-theme');
        } else {
            document.documentElement.classList.remove('day-theme');
        }
    },[dayMode])

    const rotateStyle = {
        transform: showRoute
          ? "rotate(90deg)"
          : "rotate(-90deg)",
        transition: "transform 0.4s ease"
    };

    // https://react-leaflet.js.org/docs/v4/start-introduction/
    return (
        <div className='acceuil_container'>
            <MapContainer
                center={[50, 0]}
                zoom={1}
                scrollWheelZoom={true}
                className='MapContainer'
                worldCopyJump={true}
                minZoom={3}
                zoomControl={false}
                maxBounds={[
                    [-85, -1750],
                    [85, 1750]]}
                ref={e => { setMapInstance(e) }}>

                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url={"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" }
                />

                {/* https://www.npmjs.com/package/react-leaflet-cluster */}
                <MarkerClusterGroup chunkedLoading  showCoverageOnHover={false} iconCreateFunction={createClusterCustomIcon}>
                    {markers.length > 0 ? (
                        markers.map(publication => (
                            <Marker key={publication.id} position={publication.pos}
                                icon={createColorIcon(require('./Images/' + publication.grade + ".png"))}
                                eventHandlers={{
                                    click: () => { 
                                        mapInstance.flyTo( publication.pos, mapInstance.getZoom())
                                        openPublication(publication.id) 
                                    }
                                }}>
                            </Marker>
                        ))
                    ): (
                        <p>Loading...</p>
                    )}
                </MarkerClusterGroup>

                {/* https://www.liedman.net/leaflet-routing-machine/ */}
                {destination && (position ? <RoutingMachine key={i18n.language} onRouteCalculated={setDirections} position={position} destination={destination} setDestination={setDestination} lang={i18n.language} />: "") }
                <HoverMarker position={hoverPosition} />
                {isLoggedIn && creatingMode &&  <HoverMarker position={mouseHoverPosition} />}
                
            </MapContainer>

            {/* Custom Direction Window */}
            {destination &&
            <div className='routing'>
                <div style={{display:'flex', alignItems:'center', width:'100%', justifyContent:'space-between'}}> 
                    <div onClick={() => setShowRoute(!showRoute)} style={{display:'flex', alignItems:'center', cursor:'pointer'}}>
                        <h3>{t('routing_direction')}</h3> 
                        <span className="material-symbols-outlined directionBtn" style={rotateStyle}>chevron_right</span>
                    </div>
                    {directions.length > 0 && <a href={`https://www.google.com/maps?q=${destination[0]},${destination[1]}`} target="_blank" style={{color:"var(--secondaryLight)"}}>{t('routing_google')}</a>}
                </div>
                {position ?                 
                <ul style={{display: (showRoute ? "block" : "none")}}>
                    {directions.map((step, index) => (
                        <li key={index}
                            onMouseEnter={() => setHoverPosition(step.position)}
                            onMouseLeave={() => setHoverPosition(null)}
                            onMouseDown={
                            () => {
                                    mapInstance.flyTo(step.position, mapInstance.getZoom())   
                                } 
                            }
                            style={{ cursor: "pointer" }}>
                            {step.text}
                        </li>
                    ))}
                </ul>
                :
                t('create_publication_no_location')
                }
            </div>
            }

            { !creatingPublication && !publication && 
            <span style={{color: (dayMode ?'var(--primary)' : 'var(--secondary)')}} 
            onClick={() => {setInfo(!info)}} 
            className={"material-symbols-outlined changeMode"}
            id="infoMode">
                info
            </span>}

            {info && <Info setInfo={setInfo}></Info>}

            {isLoggedIn && !creatingPublication && !publication &&
             ( 
             destination ? 
             (directions != null  && <Button variant="outlined" className='btBase add' id="cancelModify" onClick={() => {setDestination(null);}}> {t('routing_quit')} </Button>)
             : 
             <>             
                <Button variant="outlined" className='btBase add' id={creatingMode ? "cancelModify" : ''} onClick={() => {setCreatingMode(!creatingMode) }}> { !creatingMode ? t('create_publication')  : t('cancel')} </Button>
                {creatingMode && <Button variant="outlined" className='btBase' id="use_location" 
                                         onClick={() => { if(position){
                                            setCreatingMode(false)
                                            set_create_publication({"pos": position})
                                         } }}> 
                                         {position ? t('create_publication_here') : t('create_publication_no_location') + ". " + t('create_publication_no_location_click')}
                                         </Button>}
             </>
             )
             }
        </div>
    );
}

export default Map;
